import {List} from 'immutable';
import {FileUploadId} from 'records/CoachingReviewFileUpload';
import api, {callsApi, javaApiFor} from 'sources/api';
import {Call} from '@store/call-card/Call';
import Scorecard from 'records/Scorecard';
import {
  CallAsset,
  Segment,
  Speaker,
  TopicMatch,
  TranscriptBlock,
  KeywordSearchResult,
} from '@store/call-card/CallCard.types';
import {UserCallActivity} from '@store/call-card/UserCallActivity';
import {Vote} from 'records/CallDetails';
import {UserComment} from 'records/UserComment';
import {Seconds} from '@store/@types/util';
import {Matchable, Topic, TopicType, TopicId} from '@store/topic-keyword/TopicKeyword.types';
import {getSpeakerDescription} from '@components/call-card/CallSpeaker.utils';
import {TRENDLINE_COLORS} from 'components/manager-dashboard/Common';

export class CallCardSource {
  static getCall(id: FileUploadId): Promise<Call | undefined> {
    return callsApi.getJson(`/search/id_only/${id}`).then(res => {
      if (res.results.length === 0) {
        return;
      } else {
        return new Call(res.results[0]);
      }
    });
  }

  static getCallSource(id: FileUploadId): Promise<[CallAsset[], string]> {
    return callsApi.getJson(`/callcard/${id}/assets`).then(asset => [asset, id]);
  }

  static getScorecards(id: FileUploadId): Promise<[List<Scorecard>, FileUploadId]> {
    return api
      .getJson(`/file/${id}/scorecards`)
      .then(res => [List(res.scorecards.map(r => new Scorecard(r))), id]);
  }

  static getSpeakers(id: FileUploadId): Promise<[List<Speaker>, FileUploadId]> {
    return callsApi.getJson(`/callcard/${id}/speakers`).then(speakers => [
      List(
        speakers.map((speaker, index) => ({
          ...speaker,
          description: getSpeakerDescription(speaker, index + 1),
        }))
      ),
      id,
    ]);
  }

  static getTranscript(id: FileUploadId): Promise<[List<TranscriptBlock>, FileUploadId]> {
    return callsApi.getJson(`/transcript/${id}`).then(blocks => [List(blocks), id]);
  }

  static getUserCallActivity(id: FileUploadId): Promise<[List<UserCallActivity>, FileUploadId]> {
    return javaApiFor('/api/folder_call_history')
      .getJson(id)
      .then(({history}) => [List(history.map(item => new UserCallActivity(item))), id]);
  }

  static getSegments(id: FileUploadId): Promise<[List<Segment>, FileUploadId]> {
    return callsApi.getJson(`/callcard/${id}/segments`).then(segments => [List(segments), id]);
  }

  static setVote(id: FileUploadId, vote: 'Up' | 'Down'): Promise<[Vote, FileUploadId]> {
    return callsApi.postJson(`/vote/${id}`, {vote}).then(res => [res, id]);
  }

  static deleteVote(id: FileUploadId): Promise<[FileUploadId]> {
    return callsApi.delete(`/vote/${id}`).then(() => [id]);
  }

  /**
   * TODO Update to use unified comment API when it exists
   */
  static async getComments(id: FileUploadId): Promise<[List<UserComment>, FileUploadId]> {
    const commentsPromise = callsApi
      .getJson(`/comments/${id}`)
      .then(comments => comments.map(c => ({...c, startTime: c.location, endTime: c.location})));
    const highlightsPromise = callsApi.getJson(`/highlights/${id}`).then(highlights =>
      highlights.map(h => ({
        ...h,
        message: h.content,
        startTime: h.start,
        endTime: h.end,
      }))
    );
    const comments = await Promise.all([commentsPromise, highlightsPromise]);
    return [List(comments.flat().map(c => new UserComment(c))), id];
  }

  static keywordSearch(
    callId: FileUploadId,
    searchText: string
  ): Promise<List<KeywordSearchResult>> {
    return callsApi
      .getJson(`/transcript/${callId}/keyword/search`, {
        params: {search: searchText},
      })
      .then(results => {
        results.forEach(r => {
          // Fall back to word, since transcript words have no id. This allows us to dedupe later
          r.id = r.topicType === TopicType.TRANSCRIPT_WORD ? r.word : r.topicId;
        });
        return List(results);
      });
  }

  static async getMatches(
    id: FileUploadId,
    topic: Matchable
  ): Promise<[List<TopicMatch>, FileUploadId]> {
    const rawMatches =
      topic instanceof Topic || topic.topicType === TopicType.TOPIC
        ? await callsApi.getJson(`/transcript/${id}/topic/${topic.id}`)
        : topic.topicType === TopicType.KEYWORD
        ? await callsApi.getJson(`/transcript/${id}/keyword/${topic.id}`)
        : await callsApi.getJson(`/transcript/${id}/keyword`, {params: {term: topic.word}});

    const topicMatches = rawMatches.map(m => ({
      id: topic.id,
      word: m.first.word,
      speakerNumber: m.second.speakerNumber,
      startTime: m.second.startTime,
      endTime: m.second.endTime,
      chunk: m.second.chunk,
    }));
    return [List(topicMatches), id];
  }

  static setCoachMe(id: FileUploadId, coachMe: boolean) {
    return callsApi.put(`/calls/${id}/feedback_requested`, {coachMe}).then(() => [coachMe, id]);
  }

  static createListen(id: FileUploadId, start: Seconds, stop: Seconds) {
    return callsApi.postJson(`/listens/${id}`, {start, stop});
  }

  static deleteHighlight(id: FileUploadId, highlightId: string) {
    return callsApi.delete(`/highlights/${id}/highlight/${highlightId}`);
  }

  static getTopics(
    id: FileUploadId,
    extraTopicIds: List<TopicId>
  ): Promise<[List<Topic>, List<TopicId>, FileUploadId]> {
    return callsApi
      .getJson(`/transcript/${id}/topics`, {
        params: {extraTopicIds: extraTopicIds.toJS()},
      })
      .then(res => {
        const topics = res
          .map((topic, idx) => ({
            ...topic,
            color: TRENDLINE_COLORS[idx % TRENDLINE_COLORS.length],
          }))
          .map(topic => new Topic(topic));
        return [List(topics), extraTopicIds, id];
      });
  }
}
